import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Calendar, CheckCircle, XCircle, Clock, Warning, Lightning } from '@phosphor-icons/react';
import { formatDateTime } from '../lib/utils';

/**
 * Admin / Owner: List all account requests, manually approve/reject,
 * toggle auto-mode (when ON, every new request auto-allocates from stock).
 */
export default function AccountRequestsAdminPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'owner';
  const [items, setItems] = useState([]);
  const [autoMode, setAutoMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rejecting, setRejecting] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: am }] = await Promise.all([
        api.get('/account-requests'),
        api.get('/account-requests/auto-mode'),
      ]);
      setItems(list || []);
      setAutoMode(!!am.enabled);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Load fail');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleAuto = async () => {
    const next = !autoMode;
    try {
      await api.post('/account-requests/auto-mode', { enabled: next });
      setAutoMode(next);
      toast.success(next ? '⚡ Auto-mode ON — naye requests auto-allocate honge' : 'Auto-mode OFF — manual approve karna hoga');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Toggle fail');
    }
  };

  const approve = async (r) => {
    // Feb 2026 policy: backend already enforces reason on create; this is just
    // a defensive UX guard so admin sees a clear toast if a legacy request
    // somehow has no reason.
    if (!r.reason && !r.reason_code) {
      return toast.error('Reason missing — request reject karke staff ko reason daalne ke liye bolo');
    }
    if (!window.confirm(
      `${r.requester_name} ka request approve karna hai?\n\nReason: ${r.reason_code || r.reason}\nAgency: ${r.agency_name}\n\nEk replacement account allocate hoga.`
    )) return;
    try {
      const { data } = await api.post(`/account-requests/${r.id}/approve`);
      toast.success(`Approved — ${data.allocated_count}/1 account allocated`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Approve fail');
    }
  };

  const doReject = async () => {
    if (!rejecting) return;
    try {
      await api.post(`/account-requests/${rejecting.id}/reject`, { reason: rejectReason });
      toast.success('Rejected');
      setRejecting(null);
      setRejectReason('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reject fail');
    }
  };

  const StatusBadge = ({ s }) => {
    const map = {
      pending:           { c: 'bg-amber-100 text-amber-700 border-amber-300', icon: Clock, label: 'PENDING' },
      fulfilled:         { c: 'bg-emerald-100 text-emerald-700 border-emerald-300', icon: CheckCircle, label: 'FULFILLED' },
      partial:           { c: 'bg-blue-100 text-blue-700 border-blue-300', icon: Warning, label: 'PARTIAL' },
      approved_no_stock: { c: 'bg-orange-100 text-orange-700 border-orange-300', icon: Warning, label: 'NO STOCK' },
      rejected:          { c: 'bg-red-100 text-red-700 border-red-300', icon: XCircle, label: 'REJECTED' },
    };
    const cfg = map[s] || { c: 'bg-gray-100 text-gray-700 border-gray-300', icon: Clock, label: s.toUpperCase() };
    const Icon = cfg.icon;
    return <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded border ${cfg.c}`}><Icon size={10} weight="fill" /> {cfg.label}</span>;
  };

  const pending = items.filter(r => r.status === 'pending');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Account Requests</h1>
          <p className="text-sm text-gray-500 mt-0.5">Staff/sub-staff ke account requests · auto-mode toggle se hands-free allocation</p>
        </div>
        <button
          onClick={toggleAuto}
          disabled={!isAdmin}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-all ${autoMode ? 'bg-violet-600 text-white shadow-md' : 'bg-gray-100 text-gray-700 border border-gray-300'} ${!isAdmin ? 'opacity-50 cursor-not-allowed' : ''}`}
          data-testid="auto-mode-toggle"
          title={!isAdmin ? 'Only admin can change auto-mode' : ''}
        >
          <Lightning size={16} weight={autoMode ? 'fill' : 'regular'} />
          Auto-Mode: {autoMode ? 'ON' : 'OFF'}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="card-flat p-3"><div className="text-xs text-gray-500">Pending</div><div className="text-2xl font-bold text-amber-600">{pending.length}</div></div>
        <div className="card-flat p-3"><div className="text-xs text-gray-500">Total Requests</div><div className="text-2xl font-bold text-gray-900">{items.length}</div></div>
        <div className="card-flat p-3"><div className="text-xs text-gray-500">Auto-Mode</div><div className={`text-2xl font-bold ${autoMode ? 'text-violet-600' : 'text-gray-400'}`}>{autoMode ? 'ON' : 'OFF'}</div></div>
      </div>

      <div className="card-flat overflow-x-auto" data-testid="account-requests-table">
        <Table className="table-dense">
          <TableHeader>
            <TableRow>
              <TableHead>Requester</TableHead>
              <TableHead>Agency</TableHead>
              <TableHead className="text-right">Need</TableHead>
              <TableHead className="text-right">Allocated</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={8} className="text-center py-6 text-gray-500">Loading…</TableCell></TableRow> :
             items.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center py-6 text-gray-500">Koi request nahi</TableCell></TableRow> :
             items.map(r => (
              <TableRow key={r.id} data-testid={`ar-row-${r.id}`} className={r.status === 'pending' ? 'bg-amber-50/30' : ''}>
                <TableCell className="text-sm">
                  <div className="font-semibold">{r.requester_name}</div>
                  <div className="text-[10px] text-gray-500 uppercase">{r.requester_role}</div>
                </TableCell>
                <TableCell className="text-sm">{r.agency_name}</TableCell>
                <TableCell className="text-right text-sm">
                  <span className="text-violet-700 font-bold">1</span>
                  <span className="ml-1 text-[9px] text-gray-400 uppercase">acc</span>
                </TableCell>
                <TableCell className="text-right text-sm">
                  <span className={r.allocated_count >= 1 ? 'text-emerald-700 font-bold' : 'text-amber-700'}>
                    {r.allocated_count}/1
                  </span>
                </TableCell>
                <TableCell className="text-xs max-w-[260px]">
                  {r.reason_code ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-violet-50 text-violet-700 border border-violet-200 rounded px-1.5 py-0.5">
                      {r.reason_code}
                    </span>
                  ) : null}
                  {r.reason_extra && <div className="mt-0.5 text-gray-600 truncate" title={r.reason_extra}>{r.reason_extra}</div>}
                  {!r.reason_code && r.reason && <span className="text-gray-600 truncate" title={r.reason}>{r.reason}</span>}
                  {!r.reason_code && !r.reason && <span className="text-red-500 italic text-[10px]">no reason</span>}
                </TableCell>
                <TableCell><StatusBadge s={r.status} />{r.auto_approved && <span className="ml-1 text-[9px] bg-violet-100 text-violet-700 px-1 rounded font-bold">AUTO</span>}</TableCell>
                <TableCell className="text-xs text-gray-500"><Calendar size={11} className="inline mr-0.5" /> {formatDateTime(r.created_at)}</TableCell>
                <TableCell className="text-right">
                  {r.status === 'pending' && isAdmin ? (
                    <div className="flex justify-end gap-1">
                      <Button size="sm" onClick={() => approve(r)} className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`approve-${r.id}`}>
                        <CheckCircle size={12} className="mr-0.5" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setRejecting(r)} className="h-7 text-[11px] text-red-600 border-red-300 hover:bg-red-50" data-testid={`reject-${r.id}`}>
                        <XCircle size={12} className="mr-0.5" /> Reject
                      </Button>
                    </div>
                  ) : r.status === 'pending' && !isAdmin ? (
                    <span className="text-[10px] text-gray-400 italic">view-only</span>
                  ) : (
                    <span className="text-[10px] text-gray-400">{r.approved_by_name || ''}</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!rejecting} onOpenChange={(o) => !o && setRejecting(null)}>
        <DialogContent className="max-w-md" data-testid="reject-dialog">
          <DialogHeader><DialogTitle>Reject Request</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-700">
            <b>{rejecting?.requester_name}</b> ka request reject karna hai? Reason batao taki staff ko notification me dikhe.
          </p>
          <Input value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="e.g., stock khatam, baad me karenge" className="text-sm" data-testid="reject-reason-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejecting(null)} data-testid="reject-cancel">Cancel</Button>
            <Button onClick={doReject} className="bg-red-600 hover:bg-red-700 text-white" data-testid="reject-confirm">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
